import { err, isOk, ok } from "utils/result";
import type { Result } from "utils/result";

/**
 * Safely parses persisted JSON and validates the parsed value.
 * Returns failure when JSON is invalid or validator rejects the shape.
 */
export const parseStoredPreferences = <T extends object>(
  raw: string,
  isValid: (value: unknown) => value is T,
): Result<T, "invalid-preferences"> => {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isValid(parsed)) return err("invalid-preferences");
    return ok(parsed);
  } catch {
    return err("invalid-preferences");
  }
};

interface RecoverStoredPreferencesParams<T extends object> {
  companyId: number;
  storageKey: (companyId: number) => string;
  fallback: T;
  setState: (next: T) => void;
  setError: (error: Error) => void;
  parse: (raw: string) => Result<T, "invalid-preferences">;
}

/**
 * Loads persisted preferences from localStorage and recovers to fallback when invalid.
 */
export const loadStoredPreferencesWithRecovery = <T extends object>(
  params: RecoverStoredPreferencesParams<T>,
): void => {
  const { companyId, storageKey, fallback, setState, setError, parse } = params;
  const key = storageKey(companyId);
  const raw = localStorage.getItem(key);

  if (!raw) {
    setState(fallback);
    try {
      localStorage.setItem(key, JSON.stringify(fallback));
    } catch {
      setError(new Error("Could not persist fallback preferences"));
    }
    return;
  }

  const parsed = parse(raw);
  if (isOk(parsed)) {
    setState(parsed.value);
    return;
  }

  setState(fallback);
  try {
    localStorage.setItem(key, JSON.stringify(fallback));
  } catch {
    try {
      localStorage.removeItem(key);
    } catch {
      setError(new Error("Could not recover invalid preferences"));
      return;
    }
    setError(new Error("Could not persist recovered preferences"));
  }
};
